import type { DocMeta } from "@klarity/schema";
import type { ThemeTokens } from "@klarity/themes";
import type { CSSProperties, ReactNode } from "react";
import { screenVars } from "./screenVars";
import { useThemeFonts } from "./useThemeFonts";

export type CanvasDoc = { kind: "page" | "document" | "template"; title: string; meta: DocMeta };

/**
 * The two meta defaults the screen needs (C4: lang defaults to nl for client documents, numbered to true).
 * resolveMeta() is not used here: it throws while a document's required fields are still being filled in.
 */
export function screenMeta(meta: DocMeta): { lang: "en" | "nl"; numbered: boolean } {
  return { lang: meta.lang ?? (meta.audience === "client" ? "nl" : "en"), numbered: meta.numbered !== false };
}

export function ThemeCanvas({
  tokens,
  doc,
  fileUrl,
  children,
}: {
  tokens: ThemeTokens;
  doc: CanvasDoc;
  fileUrl: (src: string) => string;
  children: ReactNode;
}) {
  useThemeFonts(tokens, fileUrl);
  const meta = doc.meta;
  const { lang, numbered } = screenMeta(meta);
  return (
    <article
      className="kd-sheet"
      style={screenVars(tokens) as CSSProperties}
      lang={lang}
      data-numbered={String(numbered)}
      data-stripes={String(tokens.table.stripes)}
    >
      {doc.kind === "page" ? (
        <h1 className="kd-title">{doc.title}</h1>
      ) : (
        <header className="kd-titleblock">
          {meta.code ? <div className="kd-titleblock__code">{meta.code}</div> : null}
          <div className="kd-titleblock__subtitle">{meta.subtitle || doc.title}</div>
          {meta.docType ? <div className="kd-titleblock__doctype">{meta.docType}</div> : null}
        </header>
      )}
      {children}
    </article>
  );
}
